/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import {
    Blank,
    EditObjectRequest,
    Effect,
    FullAssignment,
    Nullable,
    Pagination,
    Reference,
    Setter,
    TechnicianEntry,
    User,
} from "../../../utils/Interfaces";
import { deleteHandler, getHandler, postHandler, stdErrHandlerFactory } from "../../../utils/Requests";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { Ref } from "react";

export function modifyTechnicianEntry(
    techEntryId: number,
    key: keyof TechnicianEntry,
    value: Nullable<string | number | boolean>,
    dispatch: Dispatch<AnyAction>,
    onTechEntryModified: Effect,
    setLoading: Setter<boolean>,
    onTechEntryModifyFailed?: Effect
) {
    postHandler<EditObjectRequest<TechnicianEntry, Nullable<string | number | boolean>>, Reference>(
        `/api/tech-entries/update/${techEntryId}`,
        {},
        dispatch,
        (response: AxiosResponse<Reference, any>) => onTechEntryModified(),
        stdErrHandlerFactory(dispatch, () => (onTechEntryModifyFailed ? onTechEntryModifyFailed() : () => {})),
        setLoading
    );
}

export function deleteTechnicianEntry(techEntryId: number, dispatch: Dispatch<AnyAction>, onTechEntryDeleted: Effect) {
    deleteHandler<Blank>(
        `/api/tech-entries/delete/${techEntryId}`,
        dispatch,
        (response: AxiosResponse<Blank, any>) => onTechEntryDeleted(),
        stdErrHandlerFactory(dispatch)
    );
}

function addMaterialToTechnicianEntry(
    techEntryId: number,
    materialId: number,
    amount: number,
    dispatch: Dispatch<AnyAction>,
    onModified: Effect
) {
    postHandler<Blank, Reference>(
        `/api/materials/trips/add/${techEntryId}/${materialId}/${amount}`,
        {},
        dispatch,
        (response: AxiosResponse<Reference, any>) => onModified(),
        stdErrHandlerFactory(dispatch)
    );
}

function deleteMaterialFromTechnicianEntry(
    techEntryId: number,
    materialId: number,
    dispatch: Dispatch<AnyAction>,
    onModified: Effect
) {
    postHandler<Blank, Reference>(
        `/api/materials/trips/remove/${techEntryId}/${materialId}`,
        {},
        dispatch,
        (response: AxiosResponse<Reference, any>) => onModified(),
        stdErrHandlerFactory(dispatch)
    );
}

export function updateMaterialForTechnicianEntry(
    techEntryId: number,
    materialId: number,
    amount: number,
    dispatch: Dispatch<AnyAction>,
    onModified: Effect
) {
    if (amount < 1) {
        deleteMaterialFromTechnicianEntry(techEntryId, materialId, dispatch, onModified);
    } else {
        addMaterialToTechnicianEntry(techEntryId, materialId, amount, dispatch, onModified);
    }
}

export function assignTechnician(
    technician: User,
    technicianEntry: TechnicianEntry,
    dispatch: Dispatch<AnyAction>,
    onTechnicianAssigned: Effect,
    setLoading: Setter<boolean>
) {
    postHandler<Blank, Reference>(
        `/api/tech-entries/assign/${technicianEntry.id}/${technician.id}`,
        {},
        dispatch,
        (response: AxiosResponse<Reference, any>) => {
            onTechnicianAssigned();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function unassignTechnician(
    technician: User,
    technicianEntry: TechnicianEntry,
    dispatch: Dispatch<AnyAction>,
    onTechnicianUnassigned: Effect,
    setLoading: Setter<boolean>
) {
    postHandler<Blank, Reference>(
        `/api/tech-entries/unassign/${technicianEntry.id}/${technician.id}`,
        {},
        dispatch,
        (response: AxiosResponse<Reference, any>) => {
            onTechnicianUnassigned();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function updateAssignments(
    technicianEntry: TechnicianEntry,
    dispatch: Dispatch<AnyAction>,
    setAssignments: Setter<FullAssignment[]>,
    setLoading: Setter<boolean>
) {
    getHandler<Pagination<FullAssignment>>(
        `/api/tech-entries/get-assignees/${technicianEntry.id}`,
        dispatch,
        (response: AxiosResponse<Pagination<FullAssignment>>) => setAssignments(response.data.results),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function setLeadTechnician(
    technician: User,
    technicianEntry: TechnicianEntry,
    dispatch: Dispatch<AnyAction>,
    onLeadTechnicianModified: Effect,
    setLoading: Setter<boolean>
) {
    postHandler<Blank, Reference>(
        `/api/tech-entries/set-lead/${technicianEntry.id}/${technician.id}`,
        {},
        dispatch,
        (response: AxiosResponse<Ref<any>>) => {
            onLeadTechnicianModified();
        },
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function deleteTechnicianChecklistItem(
    checklistItemId: number,
    dispatch: Dispatch<AnyAction>,
    onChecklistItemDeleted: Effect,
    setLoading: Setter<boolean>
) {
    deleteHandler<Blank>(
        `/api/tech-entries/checklist/delete/${checklistItemId}`,
        dispatch,
        (response: AxiosResponse<Blank, any>) => onChecklistItemDeleted(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function renameTechnicianChecklistItem(
    checklistItemId: number,
    newLabel: string,
    dispatch: Dispatch<AnyAction>,
    onChecklistItemDeleted: Effect,
    setLoading: Setter<boolean>
) {
    postHandler<Blank, Blank>(
        `/api/tech-entries/checklist/rename/${checklistItemId}`,
        { label: newLabel },
        dispatch,
        (response: AxiosResponse<Blank, any>) => onChecklistItemDeleted(),
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}
